import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminProcApi } from "../../api";
import { ProcError, ProcForm, ProcSection } from "../../components/ProcurementLayout";
import { useMutation } from "../../hooks";

const LIKELIHOOD_LABELS: Record<number, string> = { 1: "Very Low", 2: "Low", 3: "Medium", 4: "High", 5: "Very High" };

type RiskStatus = "OPEN" | "MITIGATED" | "CLOSED";

export default function RiskCreatePage() {
  const navigate = useNavigate();

  const [riskDescription, setRiskDescription] = useState("");
  const [likelihood, setLikelihood] = useState(1);
  const [impact, setImpact] = useState(1);
  const [mitigationStrategy, setMitigationStrategy] = useState("");
  const [status, setStatus] = useState<RiskStatus>("OPEN");

  const riskScore = likelihood * impact;
  const scoreColor = riskScore >= 15 ? "text-red-600" : riskScore >= 8 ? "text-amber-600" : "text-emerald-600";

  const { mutate, loading, error } = useMutation(async () => {
    if (!riskDescription.trim()) throw new Error("Risk description is required.");
    if (!mitigationStrategy.trim()) throw new Error("Mitigation strategy is required.");
    await adminProcApi.createRisk({
      riskDescription,
      likelihood,
      impact,
      mitigationStrategy,
      status,
      responsibleUserId: null,
      responsibleUsername: null,
    });
    navigate("/procurement/admin/risks");
  });

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/procurement/admin/risks" className="text-sm text-emerald-700 hover:underline">&larr; Back to Risk Register</Link>
      </div>

      <ProcForm formNo="PROC-FRM-15" title="Procurement Risk Register">
        {error && <ProcError message={error} />}

        <ProcSection title="Risk Details">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Risk Description *</label>
              <textarea value={riskDescription} onChange={(e) => setRiskDescription(e.target.value)} rows={3} className="w-full rounded-xl border px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Likelihood (1–5)</label>
              <select value={likelihood} onChange={(e) => setLikelihood(Number(e.target.value))} className="w-full rounded-xl border px-3 py-2 text-sm">
                {[1, 2, 3, 4, 5].map((v) => <option key={v} value={v}>{v} — {LIKELIHOOD_LABELS[v]}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Impact (1–5)</label>
              <select value={impact} onChange={(e) => setImpact(Number(e.target.value))} className="w-full rounded-xl border px-3 py-2 text-sm">
                {[1, 2, 3, 4, 5].map((v) => <option key={v} value={v}>{v} — {LIKELIHOOD_LABELS[v]}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-600 font-semibold">Risk Score:</span>
              <span className={`text-2xl font-black ${scoreColor}`}>{riskScore}</span>
              <span className="text-xs text-slate-500">/ 25 {riskScore >= 15 ? "(HIGH)" : riskScore >= 8 ? "(MEDIUM)" : "(LOW)"}</span>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value as RiskStatus)} className="w-full rounded-xl border px-3 py-2 text-sm">
                <option value="OPEN">OPEN</option>
                <option value="MITIGATED">MITIGATED</option>
                <option value="CLOSED">CLOSED</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Mitigation Strategy *</label>
              <textarea value={mitigationStrategy} onChange={(e) => setMitigationStrategy(e.target.value)} rows={4} className="w-full rounded-xl border px-3 py-2 text-sm" required />
            </div>
          </div>
        </ProcSection>

        <div className="flex justify-end gap-3 pt-2">
          <Link to="/procurement/admin/risks" className="rounded-xl border px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</Link>
          <button type="button" onClick={() => mutate()} disabled={loading} className="rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60">
            {loading ? "Saving…" : "Save Risk"}
          </button>
        </div>
      </ProcForm>
    </div>
  );
}
